import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatarValorBR, parsearValorBR, parsearValorColado } from "../lib/formato";
import Layout from "../components/Layout";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { linhaTravaPtax, rotuloTravaPtax } from "../lib/travaPtax";
import { PencilSimple, FloppyDisk, X, ClockCountdown, Plus, Calculator, FileXls, FileArrowUp } from "@phosphor-icons/react";

const FIXED_OPS = [
  { opId: "abertura", label: "ABERTURA", auto: false, flexColuna: true, editMontante: true, editTaxa: true },
  { opId: "trava_mesa", label: "TRAVA MESA", auto: true, coluna: "venda" },
  { opId: "trava_ptax", label: "TRAVA PTAX", auto: true, coluna: "compra" },
  { opId: "ndf_liq_venda", label: "NDF LIQUIDADA — VENDA", auto: false, coluna: "venda", ptaxField: "cotacao_venda", editMontante: true },
  { opId: "ndf_liq_compra", label: "NDF LIQUIDADA — COMPRA", auto: false, coluna: "compra", ptaxField: "cotacao_venda", editMontante: true },
  { opId: "ndf_venda", label: "NDF — VENDA (nova)", auto: false, coluna: "venda", fromCalculator: true },
  { opId: "ndf_compra", label: "NDF — COMPRA (nova)", auto: false, coluna: "compra", fromCalculator: true },
  { opId: "pagamento_invoice", label: "PAGAMENTO — INVOICE", auto: false, coluna: "compra", fromCalculator: true },
  { opId: "recebimento_cambio", label: "RECEBIMENTO CÂMBIO", auto: false, coluna: "venda", fromCalculator: true },
  { opId: "contas_a_receber_usd", label: "CONTAS A RECEBER - USD", auto: false, coluna: "venda", ptaxField: "cotacao_venda", editMontante: true },
];

const fmt4 = (n) => n != null ? n.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : "—";
const fmt2 = (n) => n != null ? n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
const fmtDateBr = (iso) => { if (!iso) return ''; const [a, m, d] = iso.split('-'); return `${d}/${m}/${a}`; };

const numInput = "w-28 bg-[#061729] border border-[#40B6A2]/50 rounded-sm px-2 py-1 text-right text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] block ml-auto";
const calcInput = "w-full bg-[#061729] border border-[#1e3a55] rounded-sm px-1.5 py-1 text-right text-xs font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]";

// onPaste dos campos de montante: a colagem substitui o campo inteiro pelo
// canônico deduzido do clipboard, porque o texto colado pode vir em formato
// americano (240927.59) ou pt-BR (240.927,59) e só ali dá para distinguir —
// no onChange o ponto é sempre milhar. Canônico null = nada numérico no
// clipboard; aí deixa o onChange padrão tratar.
const colarValor = (aplicar) => (e) => {
  const canonico = parsearValorColado(e.clipboardData.getData("text"));
  if (canonico === null) return;
  e.preventDefault();
  aplicar(canonico);
};

let localIdCounter = 0;
const newLocalId = () => `local_${++localIdCounter}`;

// Preferência de exibição da taxa das linhas TRAVA MESA, PAGAMENTO — INVOICE
// e RECEBIMENTO CÂMBIO: spot ponderado por volume em vez da taxa
// negociada/taxa_final ponderada por volume. Persiste por navegador. Nome da
// chave, da variável e o rótulo "Trava Mesa" na tela mantidos por
// compatibilidade com o valor já salvo no navegador dos usuários — o toggle
// deixou de ser exclusivo da TRAVA MESA, mas renomear a chave apagaria a
// preferência de todo mundo.
const TRAVA_MESA_SIMPLES_KEY = "brf_trava_mesa_spot_simples";

export default function PosicaoDia() {
  const { user } = useAuth();
  const readOnly = user?.role === "gestao";
  const hoje = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(hoje);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [ndfLines, setNdfLines] = useState([]);
  const [removedDbIds, setRemovedDbIds] = useState([]);
  const [savingNdf, setSavingNdf] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [removedInvoiceDbIds, setRemovedInvoiceDbIds] = useState([]);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [recebimentoLines, setRecebimentoLines] = useState([]);
  const [removedRecebimentoDbIds, setRemovedRecebimentoDbIds] = useState([]);
  const [savingRecebimento, setSavingRecebimento] = useState(false);
  const [ptaxCotacoes, setPtaxCotacoes] = useState([]);
  const [calcOpen, setCalcOpen] = useState(false);
  const primeiroDoMes = hoje.slice(0, 8) + "01";
  const [exportOpen, setExportOpen] = useState(false);
  const [exportInicio, setExportInicio] = useState(primeiroDoMes);
  const [exportFim, setExportFim] = useState(hoje);
  const [exportLoading, setExportLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [spot, setSpot] = useState(null);
  const [spotIntervalMs, setSpotIntervalMs] = useState(40000);
  const [travaMesaSimples, setTravaMesaSimples] = useState(
    () => localStorage.getItem(TRAVA_MESA_SIMPLES_KEY) === "1"
  );
  const [ndfBancario, setNdfBancario] = useState(null);
  const [ajuste, setAjuste] = useState(null);
  const [editingAjuste, setEditingAjuste] = useState(false);
  const [ajusteValor, setAjusteValor] = useState("");
  const [ajusteColuna, setAjusteColuna] = useState("venda");
  const [savingAjuste, setSavingAjuste] = useState(false);
  const [ajuste2, setAjuste2] = useState(null);
  const [editingAjuste2, setEditingAjuste2] = useState(false);
  const [ajuste2Valor, setAjuste2Valor] = useState("");
  const [ajuste2Coluna, setAjuste2Coluna] = useState("venda");
  const [savingAjuste2, setSavingAjuste2] = useState(false);
  const [calc, setCalc] = useState({
    vp1: "", vf1: "",
    vp2: "", taxa2: "",
    periodo: "",
    dataInicio: "", dataFim: "",
  });

  const setF = (f, v) => setCalc(c => ({ ...c, [f]: v }));

  const setDateAndRecalc = (field, value) => {
    setCalc(c => {
      const next = { ...c, [field]: value };
      const d1 = next.dataInicio ? new Date(next.dataInicio) : null;
      const d2 = next.dataFim ? new Date(next.dataFim) : null;
      if (d1 && d2 && d2 >= d1) {
        const dias = (d2 - d1) / (1000 * 60 * 60 * 24);
        next.periodo = (dias / 30).toFixed(4);
      }
      return next;
    });
  };

  const vp1N = calc.vp1 !== "" ? Number(calc.vp1) : null;
  const vf1N = calc.vf1 !== "" ? Number(calc.vf1) : null;
  const periN = calc.periodo !== "" ? Number(calc.periodo) : null;
  const am1 = (vp1N && vp1N > 0 && vf1N && vf1N > 0 && periN && periN > 0)
    ? Math.pow(vf1N / vp1N, 1 / periN) - 1
    : null;
  const aa1 = am1 != null ? Math.pow(1 + am1, 12) - 1 : null;

  const vp2N = calc.vp2 !== "" ? Number(calc.vp2) : null;
  const t2N = calc.taxa2 !== "" ? Number(calc.taxa2) / 100 : null;
  const vf2Computed = (vp2N && vp2N > 0 && t2N != null && periN && periN > 0)
    ? vp2N * Math.pow(1 + t2N, periN)
    : null;
  const aa2 = t2N != null ? Math.pow(1 + t2N, 12) - 1 : null;

  const usarSpotCalc = async () => {
    try {
      const { data: spotData } = await api.get("/cotacao/spot");
      const v = spotData.spot != null ? String(spotData.spot.toFixed(4)) : "";
      setCalc(c => ({ ...c, vp1: v, vp2: v }));
      toast.success("Spot atual carregado");
    } catch {
      toast.error("Erro ao buscar spot");
    }
  };

  const limparCalc = () => setCalc({ vp1: "", vf1: "", vp2: "", taxa2: "", periodo: "", dataInicio: "", dataFim: "" });

  const handleExportar = async () => {
    if (!exportInicio || !exportFim) { toast.error("Preencha as datas"); return; }
    if (exportFim < exportInicio) { toast.error("Data fim deve ser >= data início"); return; }
    setExportLoading(true);
    try {
      const resp = await api.get("/posicao/exportar", {
        params: { dataInicio: exportInicio, dataFim: exportFim, spotSimples: travaMesaSimples ? 1 : 0 },
        responseType: "blob",
      });
      const di = exportInicio.replace(/-/g, "").slice(2);
      const df = exportFim.replace(/-/g, "").slice(2);
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `posicao_${di}_${df}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch {
      toast.error("Erro ao gerar exportação");
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportarNdf = async () => {
    if (!importFile) { toast.error("Selecione um arquivo .xlsx"); return; }
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", importFile);
      const { data: resumo } = await api.post("/posicao/importar-ndf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const linhas = (resumo.datas || [])
        .map((d) => `${fmtDateBr(d.data)}: venda USD ${fmt2(d.vendas_usd)} / compra USD ${fmt2(d.compras_usd)}`)
        .join(" | ");
      toast.success(`NDF importado — ${linhas}`);

      // O mesmo arquivo alimenta o painel de NDF em Aberto; o backend importa
      // os dois em uma transação só e devolve o resultado de cada um.
      const nb = resumo.ndf_bancario || {};
      if (nb.erro) {
        toast.error(`Posições dos bancos não importadas: ${nb.erro}`);
      } else if (nb.importados != null) {
        const avisos = [];
        if (nb.sem_acao > 0) avisos.push(`${nb.sem_acao} sem Buy/Sell (fora do ajuste)`);
        if (nb.ignoradas > 0) avisos.push(`${nb.ignoradas} ignorada(s)`);
        toast.success(
          `NDF em Aberto — ${nb.importados} posição(ões) dos bancos` +
          (avisos.length ? ` (${avisos.join(", ")})` : "")
        );
      }

      setImportOpen(false);
      setImportFile(null);
      if ((resumo.datas || []).some((d) => d.data === selectedDate)) load();
      loadNdfBancario();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao importar NDF");
    } finally {
      setImportLoading(false);
    }
  };

  const fmtPct = (n, dec = 4) => n != null ? `${(n * 100).toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec })}%` : "—";

  // Recarrega a cada troca de data: o ajuste é recalculado no backend com a
  // PTAX da data em exibição, não com um valor salvo no import.
  const loadNdfBancario = useCallback(async () => {
    try {
      const { data } = await api.get("/ndf-bancario", { params: { data: selectedDate } });
      setNdfBancario(data);
    } catch {
      // silencioso — painel só aparece quando há registros importados
    }
  }, [selectedDate]);

  useEffect(() => { loadNdfBancario(); }, [loadNdfBancario]);

  const loadCotacoes = useCallback(async () => {
    try {
      const { data } = await api.get("/ptax/cotacoes-dia", { params: { data: selectedDate } });
      setPtaxCotacoes(data);
    } catch {
      // silencioso — cotações podem não existir para datas sem movimento
    }
  }, [selectedDate]);

  useEffect(() => {
    loadCotacoes();
    const t = setInterval(loadCotacoes, 1800000);
    return () => clearInterval(t);
  }, [loadCotacoes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posicao-dia", { params: { data: selectedDate } });
      setDados(data);
      const invRecords = (data.registros || []).filter((r) => r.operacao === "pagamento_invoice");
      setInvoiceLines(invRecords.map((r) => {
        // banco armazenado em coluna como "compra|NomeBanco"
        const parts = (r.coluna || "compra").split("|");
        return {
          localId: newLocalId(),
          dbId: r.id,
          valor_usd: r.montante_usd != null ? String(Math.abs(r.montante_usd)) : "",
          taxa: r.taxa_compra != null ? String(r.taxa_compra) : "",
          spot: r.spot != null ? String(r.spot) : "",
          banco: parts[1] || "",
        };
      }));
      setRemovedInvoiceDbIds([]);

      const recRecords = (data.registros || []).filter((r) => r.operacao === "recebimento_cambio");
      // Math.abs na leitura: o banco guarda negativo (a linha é venda), a
      // calculadora edita o valor absoluto. Espelha o invoice, trocando
      // taxa_compra por taxa_venda — e sem banco, que esta linha não tem.
      setRecebimentoLines(recRecords.map((r) => ({
        localId: newLocalId(),
        dbId: r.id,
        valor_usd: r.montante_usd != null ? String(Math.abs(r.montante_usd)) : "",
        taxa: r.taxa_venda != null ? String(r.taxa_venda) : "",
        spot: r.spot != null ? String(r.spot) : "",
      })));
      setRemovedRecebimentoDbIds([]);

      const ndfRecords = (data.registros || []).filter(
        (r) => r.operacao === "ndf_nova_venda" || r.operacao === "ndf_nova_compra"
      );
      setNdfLines(
        ndfRecords.map((r) => ({
          localId: newLocalId(),
          dbId: r.id,
          op: r.operacao === "ndf_nova_venda" ? "VENDA" : "COMPRA",
          valor: r.montante_usd != null ? String(Math.abs(r.montante_usd)) : "",
          spot: r.spot != null ? String(r.spot) : "",
          taxa_termo: r.taxa_termo != null ? String(r.taxa_termo) : "",
          vencimento: r.data_vencimento || "",
        }))
      );
      setRemovedDbIds([]);
      const ajusteRecord = (data.registros || []).find((r) => r.operacao === "ajuste_manual");
      setAjuste(ajusteRecord || null);
      const ajuste2Record = (data.registros || []).find((r) => r.operacao === "ajuste_manual_2");
      setAjuste2(ajuste2Record || null);
      setLastUpdate(new Date());
    } catch {
      toast.error("Erro ao carregar posição do dia");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { load(); }, [load]);

  const isEditing = editingId || editingAjuste || editingAjuste2 || calcOpen || exportOpen || importOpen || saving || savingNdf || savingInvoice || savingRecebimento || savingAjuste || savingAjuste2 || importLoading;

  useEffect(() => {
    const t = setInterval(() => {
      if (!isEditing) load();
    }, 180000);
    return () => clearInterval(t);
  }, [load, isEditing]);

  useEffect(() => {
    let cancelled = false;
    api.get("/parametros/publico").then((r) => {
      if (cancelled) return;
      const ms = r.data?.cotacao_intervalo_ms;
      if (ms && ms > 0) setSpotIntervalMs(ms);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchSpot = async () => {
      try {
        const { data } = await api.get("/cotacao/spot");
        setSpot(data.spot ?? null);
      } catch {
        setSpot(null);
      }
    };
    fetchSpot();
    const t = setInterval(fetchSpot, spotIntervalMs);
    return () => clearInterval(t);
  }, [spotIntervalMs]);

  const ndfCalc = useMemo(() => {
    const vendaLines = ndfLines.filter((l) => l.op === "VENDA" && l.valor !== "" && Number(l.valor) > 0);
    const compraLines = ndfLines.filter((l) => l.op === "COMPRA" && l.valor !== "" && Number(l.valor) > 0);
    const somaVenda = vendaLines.reduce((s, l) => s + Number(l.valor), 0);
    const somaCompra = compraLines.reduce((s, l) => s + Number(l.valor), 0);
    const vendaTaxa =
      somaVenda > 0 && vendaLines.every((l) => l.spot !== "")
        ? vendaLines.reduce((s, l) => s + Number(l.valor) * Number(l.spot), 0) / somaVenda
        : null;
    const compraTaxa =
      somaCompra > 0 && compraLines.every((l) => l.spot !== "")
        ? compraLines.reduce((s, l) => s + Number(l.valor) * Number(l.spot), 0) / somaCompra
        : null;
    return { vendaMontante: somaVenda > 0 ? -somaVenda : null, vendaTaxa, somaVenda, compraMontante: somaCompra > 0 ? somaCompra : null, compraTaxa, somaCompra };
  }, [ndfLines]);

  const invoiceCalc = useMemo(() => {
    const valid = invoiceLines.filter((l) => l.valor_usd !== "" && Number(l.valor_usd) > 0);
    const soma = valid.reduce((s, l) => s + Number(l.valor_usd), 0);
    const taxa = soma > 0 && valid.every((l) => l.taxa !== "")
      ? valid.reduce((s, l) => s + Number(l.valor_usd) * Number(l.taxa), 0) / soma
      : null;
    // Tudo-ou-nada de propósito: uma linha sem spot zera o campo em vez de
    // exibir uma média meio spot, meio taxa negociada sem avisar. Só para
    // conferência na linha de totais. A ponderada spot que vale é a do
    // export, que soma o invoice às demais linhas do dia.
    const spotPond = soma > 0 && valid.every((l) => l.spot !== "")
      ? valid.reduce((s, l) => s + Number(l.valor_usd) * Number(l.spot), 0) / soma
      : null;
    return { montante: soma > 0 ? soma : null, taxa, spotPond, soma };
  }, [invoiceLines]);

  const recebimentoCalc = useMemo(() => {
    const valid = recebimentoLines.filter((l) => l.valor_usd !== "" && Number(l.valor_usd) > 0);
    const soma = valid.reduce((s, l) => s + Number(l.valor_usd), 0);
    const taxa = soma > 0 && valid.every((l) => l.taxa !== "")
      ? valid.reduce((s, l) => s + Number(l.valor_usd) * Number(l.taxa), 0) / soma
      : null;
    // Mesmo tudo-ou-nada do invoice: uma linha sem spot zera o campo em vez de
    // exibir uma média meio spot, meio taxa negociada sem avisar. Só para
    // conferência na linha de totais — a ponderada que entra no cálculo é a de
    // buildRows, que tem fallback por linha.
    const spotPond = soma > 0 && valid.every((l) => l.spot !== "")
      ? valid.reduce((s, l) => s + Number(l.valor_usd) * Number(l.spot), 0) / soma
      : null;
    // Montante SEMPRE negativo, diferente do invoice: a linha é venda, e a
    // convenção do módulo é venda gravar negativo (server.py:normalizar_ajuste).
    // O sinal nasce aqui e é reaplicado no payload de saveRecebimento, para o
    // caso de alguém digitar um valor já negativo.
    return { montante: soma > 0 ? -soma : null, taxa, spotPond, soma };
  }, [recebimentoLines]);

  const aguardandoEfetivo = dados?.aguardando_ptax ?? false;
  const ptaxEfetivo = dados?.ptax ?? null;
  const ptaxFech = (!dados?.ptax_parcial && !dados?.ptax_fallback_data && ptaxEfetivo)
    ? ptaxEfetivo.cotacao_venda : null;
  // PTAX D-1 — usado apenas pelo 1º slot de Ajuste Manual (ajuste_manual).
  // Diferente de ptaxFech, NÃO rejeita ptax_fallback_data — e isso é
  // intencional, não descuido: D-1 aqui é dia corrido, então em toda
  // segunda-feira (e no dia seguinte a feriado) o dia anterior não tem PTAX
  // própria e só o fallback do resolver chega ao último fechamento anterior.
  // Rejeitar o fallback zeraria a linha de Ajuste Manual uma vez por semana.
  // ptax_parcial segue rejeitado: intraday não é fechamento.
  const ptaxD1Efetivo = dados?.ptax_d1?.ptax ?? null;
  const ptaxFechD1 = (!dados?.ptax_d1?.ptax_parcial && ptaxD1Efetivo)
    ? ptaxD1Efetivo.cotacao_venda : null;
  // Taxa gravada na própria linha sobrepõe a PTAX D-1. Não há tela para isso:
  // é correção pontual feita direto no banco (ex.: 26/10/2026, PTAX 5,5007).
  const ajusteTaxaManual = ajuste?.taxa_compra ?? ajuste?.taxa_venda ?? null;
  const ptaxAjuste = ajusteTaxaManual ?? ptaxFechD1;

  const buildRows = () => {
    if (!dados) return [];
    const reg = dados.registros || [];
    return FIXED_OPS.map((op) => {
      if (op.opId === "trava_mesa") {
        const tm = dados.trava_mesa;
        // Toggle escolhe o campo ponderado por volume: spot ou taxa_final.
        // Fallback em taxa_venda cobre resposta de backend antigo.
        const taxaTm = travaMesaSimples
          ? (tm?.taxa_venda_simples ?? null)
          : (tm?.taxa_venda_ponderada ?? tm?.taxa_venda ?? null);
        return { ...op, montante_usd: tm?.montante_usd ?? null, taxa_venda: taxaTm, taxa_compra: null, dbId: null, quantidade_travas: tm?.quantidade_travas ?? 0 };
      }
      if (op.opId === "trava_ptax") {
        // Taxa vem pronta do backend (PTAX compra D-1); sem ptaxField, para
        // não herdar a PTAX do dia nem o rótulo das linhas de PTAX D0.
        return { ...op, ...linhaTravaPtax(dados.trava_ptax), dbId: null };
      }
      if (op.fromCalculator) {
        if (op.opId === "ndf_venda") return { ...op, montante_usd: ndfCalc.vendaMontante, taxa_venda: ndfCalc.vendaTaxa, taxa_compra: null, dbId: null };
        if (op.opId === "ndf_compra") return { ...op, montante_usd: ndfCalc.compraMontante, taxa_venda: null, taxa_compra: ndfCalc.compraTaxa, dbId: null };
        // Assimetria consciente com a NDF acima: lá a taxa exibida é o spot
        // (ndfCalc pondera por spot e a taxa a termo fica só em taxa_termo);
        // aqui, por padrão, é a taxa negociada com o banco. Toggle ativo
        // troca para a ponderada spot — e essa troca entra na Ponderada
        // Compra e no Resultado do dia, igual ao que o toggle já faz há mais
        // tempo com a TRAVA MESA e a Ponderada Venda.
        //
        // O fallback é POR LINHA (spot em branco usa a taxa negociada da
        // própria linha), critério idêntico ao do export (server.py, bloco
        // pagamento_invoice) — é o que mantém tela e Excel iguais num invoice
        // com spot preenchido em parte das linhas. Por isso não dá para usar
        // invoiceCalc.spotPond aqui: ele é tudo-ou-nada, e um invoice misto
        // cairia inteiro na taxa negociada enquanto o Excel ponderaria.
        if (op.opId === "pagamento_invoice") {
          let taxaInvoice = invoiceCalc.taxa;
          if (travaMesaSimples) {
            const valid = invoiceLines.filter((l) => l.valor_usd !== "" && Number(l.valor_usd) > 0);
            const soma = valid.reduce((s, l) => s + Number(l.valor_usd), 0);
            if (soma > 0 && valid.every((l) => l.spot !== "" || l.taxa !== "")) {
              taxaInvoice = valid.reduce((s, l) => s + Number(l.valor_usd) * Number(l.spot !== "" ? l.spot : l.taxa), 0) / soma;
            }
          }
          return { ...op, montante_usd: invoiceCalc.montante, taxa_venda: null, taxa_compra: taxaInvoice, dbId: null };
        }
        // RECEBIMENTO CÂMBIO: mesmo desenho do invoice logo acima, do outro
        // lado da tabela. Entra na Ponderada VENDA (e por ela no Resultado do
        // dia), com o mesmo fallback POR LINHA e o mesmo critério do export
        // (server.py, OPS_COM_SPOT_PROPRIO) — é o que mantém tela e Excel
        // iguais num recebimento com spot preenchido em parte das linhas.
        if (op.opId === "recebimento_cambio") {
          let taxaReceb = recebimentoCalc.taxa;
          if (travaMesaSimples) {
            const valid = recebimentoLines.filter((l) => l.valor_usd !== "" && Number(l.valor_usd) > 0);
            const soma = valid.reduce((s, l) => s + Number(l.valor_usd), 0);
            if (soma > 0 && valid.every((l) => l.spot !== "" || l.taxa !== "")) {
              taxaReceb = valid.reduce((s, l) => s + Number(l.valor_usd) * Number(l.spot !== "" ? l.spot : l.taxa), 0) / soma;
            }
          }
          return { ...op, montante_usd: recebimentoCalc.montante, taxa_venda: taxaReceb, taxa_compra: null, dbId: null };
        }
      }
      const saved = reg.find((r) => r.operacao === op.opId);
      let taxa_venda = saved?.taxa_venda ?? null;
      let taxa_compra = saved?.taxa_compra ?? null;
      if (op.ptaxField && saved?.montante_usd != null) {
        const ptaxVal = ptaxEfetivo?.[op.ptaxField] ?? null;
        if (op.coluna === "compra") taxa_compra = ptaxVal;
        else taxa_venda = ptaxVal;
      }
      const coluna = saved?.coluna ?? op.coluna ?? "venda";
      return { ...op, coluna, montante_usd: saved?.montante_usd ?? null, taxa_venda, taxa_compra, dbId: saved?.id ?? null };
    });
  };

  const rows = buildRows();
  const totalMontante = rows.reduce((s, r) => s + (r.montante_usd ?? 0), 0) + (ajuste?.montante_usd ?? 0) + (ajuste2?.montante_usd ?? 0);
  const effAjusteColuna = editingAjuste ? ajusteColuna : (ajuste?.coluna ?? "venda");
  const effAjuste2Coluna = editingAjuste2 ? ajuste2Coluna : (ajuste2?.coluna ?? "venda");

  let somaVV = 0, somaBV = 0, somaVC = 0, somaBC = 0;
  for (const r of rows) {
    if (!r.montante_usd) continue;
    const abs = Math.abs(r.montante_usd);
    if (r.coluna === "venda" && r.taxa_venda) { somaVV += abs; somaBV += abs * r.taxa_venda; }
    if (r.coluna === "compra" && r.taxa_compra) { somaVC += abs; somaBC += abs * r.taxa_compra; }
  }
  if (ajuste?.montante_usd && ptaxAjuste) {
    const absAj = Math.abs(ajuste.montante_usd);
    const colAj = ajuste.coluna ?? "venda";
    if (colAj === "venda") { somaVV += absAj; somaBV += absAj * ptaxAjuste; }
    if (colAj === "compra") { somaVC += absAj; somaBC += absAj * ptaxAjuste; }
  }
  if (ajuste2?.montante_usd && ptaxFech) {
    const absAj2 = Math.abs(ajuste2.montante_usd);
    const colAj2 = ajuste2.coluna ?? "venda";
    if (colAj2 === "venda") { somaVV += absAj2; somaBV += absAj2 * ptaxFech; }
    if (colAj2 === "compra") { somaVC += absAj2; somaBC += absAj2 * ptaxFech; }
  }
  const pondVenda = somaVV > 0 ? somaBV / somaVV : null;
  const pondCompra = somaVC > 0 ? somaBC / somaVC : null;
  const resultadoPond = (pondVenda != null && pondCompra != null && pondVenda !== 0)
    ? ((pondCompra / pondVenda) - 1) * -1
    : null;

  const startEdit = (row) => {
    setEditingId(row.opId);
    setEditValues({
      montante_usd: row.montante_usd != null ? String(Math.abs(row.montante_usd)) : "",
      taxa_venda: row.taxa_venda ?? "",
      taxa_compra: row.taxa_compra ?? "",
      coluna: row.coluna ?? "venda",
    });
  };

  const saveRow = async (row) => {
    setSaving(true);
    try {
      const col = row.flexColuna ? editValues.coluna : (row.coluna ?? "venda");
      const montanteRaw = editValues.montante_usd !== "" ? Math.abs(Number(editValues.montante_usd)) : null;
      const montanteFinal = montanteRaw != null ? (col === "venda" ? -montanteRaw : montanteRaw) : null;
      await api.post("/posicao-dia", {
        id: row.dbId, data: selectedDate, operacao: row.opId,
        montante_usd: montanteFinal,
        taxa_venda: (col === "venda" && !row.ptaxField && editValues.taxa_venda !== "") ? Number(editValues.taxa_venda) : null,
        taxa_compra: (col === "compra" && !row.ptaxField && editValues.taxa_compra !== "") ? Number(editValues.taxa_compra) : null,
        coluna: col,
      });
      toast.success("Lançamento salvo");
      setEditingId(null);
      load();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const saveAjuste = async () => {
    if (ajusteValor === "") { toast.error("Informe o valor do ajuste"); return; }
    const valorNum = Number(ajusteValor);
    if (isNaN(valorNum)) { toast.error("Valor inválido"); return; }
    // Sinal vem do toggle, não do que foi digitado — mesma normalização que
    // saveRow aplica às demais linhas (venda negativo, compra positivo). Sem
    // isto, digitar um valor positivo com o toggle em Venda gravava montante
    // e coluna em lados opostos, e o volume ia somar no lado errado.
    const valorFinal = ajusteColuna === "venda" ? -Math.abs(valorNum) : Math.abs(valorNum);
    setSavingAjuste(true);
    try {
      const { data: saved } = await api.put("/posicao/ajuste",
        { descricao: "MOVIMENTAÇÃO ABERTURA", valor: valorFinal, coluna: ajusteColuna },
        { params: { data: selectedDate } }
      );
      setAjuste(saved);
      setEditingAjuste(false);
      toast.success("Ajuste manual salvo");
      load();
    } catch { toast.error("Erro ao salvar ajuste"); }
    finally { setSavingAjuste(false); }
  };

  const clearAjuste = async () => {
    if (!ajuste?.id) { setEditingAjuste(false); setAjusteValor(""); return; }
    try {
      await api.delete(`/posicao-dia/${ajuste.id}`);
      setAjuste(null);
      setEditingAjuste(false);
      setAjusteValor("");
      toast.success("Ajuste removido");
      load();
    } catch { toast.error("Erro ao remover ajuste"); }
  };

  const saveAjuste2 = async () => {
    if (ajuste2Valor === "") { toast.error("Informe o valor do ajuste"); return; }
    const valorNum = Number(ajuste2Valor);
    if (isNaN(valorNum)) { toast.error("Valor inválido"); return; }
    // Sinal vem do toggle, não do que foi digitado — mesma normalização que
    // saveRow aplica às demais linhas (venda negativo, compra positivo). Sem
    // isto, digitar um valor positivo com o toggle em Venda gravava montante
    // e coluna em lados opostos, e o volume ia somar no lado errado.
    const valorFinal = ajuste2Coluna === "venda" ? -Math.abs(valorNum) : Math.abs(valorNum);
    setSavingAjuste2(true);
    try {
      const { data: saved } = await api.put("/posicao/ajuste2",
        { descricao: "MOVIMENTAÇÃO FECHAMENTO", valor: valorFinal, coluna: ajuste2Coluna },
        { params: { data: selectedDate } }
      );
      setAjuste2(saved);
      setEditingAjuste2(false);
      toast.success("Ajuste manual salvo");
      load();
    } catch { toast.error("Erro ao salvar ajuste"); }
    finally { setSavingAjuste2(false); }
  };

  const clearAjuste2 = async () => {
    if (!ajuste2?.id) { setEditingAjuste2(false); setAjuste2Valor(""); return; }
    try {
      await api.delete(`/posicao-dia/${ajuste2.id}`);
      setAjuste2(null);
      setEditingAjuste2(false);
      setAjuste2Valor("");
      toast.success("Ajuste removido");
      load();
    } catch { toast.error("Erro ao remover ajuste"); }
  };

  const clearRow = async (row) => {
    if (!row.dbId) return;
    try {
      await api.delete(`/posicao-dia/${row.dbId}`);
      toast.success("Lançamento removido");
      load();
    } catch { toast.error("Erro ao remover"); }
  };

  const toggleTravaMesaSimples = (v) => {
    setTravaMesaSimples(v);
    localStorage.setItem(TRAVA_MESA_SIMPLES_KEY, v ? "1" : "0");
  };

  const getTotalBrl = (row) => {
    if (!row.montante_usd) return null;
    const taxa = row.coluna === "venda" ? row.taxa_venda : row.taxa_compra;
    if (!taxa) return null;
    return row.montante_usd * taxa;
  };

  const addNdfLine = () => setNdfLines((prev) => [...prev, { localId: newLocalId(), dbId: null, op: "VENDA", valor: "", spot: "", taxa_termo: "", vencimento: "" }]);

  const updateNdfLine = (localId, field, value) => setNdfLines((prev) => prev.map((l) => l.localId === localId ? { ...l, [field]: value } : l));

  const removeNdfLine = (localId) => {
    setNdfLines((prev) => {
      const line = prev.find((l) => l.localId === localId);
      if (line?.dbId) setRemovedDbIds((ids) => [...ids, line.dbId]);
      return prev.filter((l) => l.localId !== localId);
    });
  };

  // Spot pré-preenchido com a cotação viva do topo da tela (mesmo número do
  // botão "Usar Spot Atual"), já que o pagamento é lançado no momento em que
  // acontece. Continua editável, e apagar o campo é válido.
  const addInvoiceLine = () => setInvoiceLines((prev) => [...prev, { localId: newLocalId(), dbId: null, valor_usd: "", taxa: "", spot: spot != null ? spot.toFixed(4) : "", banco: "" }]);

  const updateInvoiceLine = (localId, field, value) => setInvoiceLines((prev) => prev.map((l) => l.localId === localId ? { ...l, [field]: value } : l));

  const removeInvoiceLine = (localId) => {
    setInvoiceLines((prev) => {
      const line = prev.find((l) => l.localId === localId);
      if (line?.dbId) setRemovedInvoiceDbIds((ids) => [...ids, line.dbId]);
      return prev.filter((l) => l.localId !== localId);
    });
  };

  const saveInvoice = async () => {
    setSavingInvoice(true);
    try {
      for (const dbId of removedInvoiceDbIds) await api.delete(`/posicao-dia/${dbId}`);
      const updatedLines = [];
      for (const line of invoiceLines) {
        const valor = line.valor_usd !== "" ? Number(line.valor_usd) : null;
        const { data: saved } = await api.post("/posicao-dia", {
          id: line.dbId || undefined,
          data: selectedDate,
          operacao: "pagamento_invoice",
          montante_usd: valor,
          taxa_compra: line.taxa !== "" ? Number(line.taxa) : null,
          // Sempre no payload, mesmo vazio: o POST com id sobrescreve a coluna
          // com o que vier (server.py:3934), então omitir apagava o spot ao
          // reeditar qualquer outro campo da linha.
          spot: line.spot !== "" ? Number(line.spot) : null,
          coluna: line.banco ? `compra|${line.banco}` : "compra",
        });
        updatedLines.push({ ...line, dbId: saved.id });
      }
      setInvoiceLines(updatedLines);
      setRemovedInvoiceDbIds([]);
      toast.success("Invoice salvo");
      load();
    } catch { toast.error("Erro ao salvar invoice"); }
    finally { setSavingInvoice(false); }
  };

  // Spot pré-preenchido com a cotação viva do topo, mesma razão do invoice: o
  // recebimento é lançado no momento em que acontece. Editável, e apagar vale.
  const addRecebimentoLine = () => setRecebimentoLines((prev) => [...prev, { localId: newLocalId(), dbId: null, valor_usd: "", taxa: "", spot: spot != null ? spot.toFixed(4) : "" }]);

  const updateRecebimentoLine = (localId, field, value) => setRecebimentoLines((prev) => prev.map((l) => l.localId === localId ? { ...l, [field]: value } : l));

  const removeRecebimentoLine = (localId) => {
    setRecebimentoLines((prev) => {
      const line = prev.find((l) => l.localId === localId);
      if (line?.dbId) setRemovedRecebimentoDbIds((ids) => [...ids, line.dbId]);
      return prev.filter((l) => l.localId !== localId);
    });
  };

  const saveRecebimento = async () => {
    setSavingRecebimento(true);
    try {
      for (const dbId of removedRecebimentoDbIds) await api.delete(`/posicao-dia/${dbId}`);
      const updatedLines = [];
      for (const line of recebimentoLines) {
        const valor = line.valor_usd !== "" ? Number(line.valor_usd) : null;
        const { data: saved } = await api.post("/posicao-dia", {
          id: line.dbId || undefined,
          data: selectedDate,
          operacao: "recebimento_cambio",
          // -Math.abs, não -valor: o sinal é da COLUNA, não do que foi
          // digitado. Digitar 100.000 ou -100.000 grava -100.000 nos dois
          // casos. Mesmo padrão de saveNdfLines para a perna de venda.
          montante_usd: valor != null ? -Math.abs(valor) : null,
          taxa_venda: line.taxa !== "" ? Number(line.taxa) : null,
          // Sempre no payload, mesmo vazio: o POST com id sobrescreve a coluna
          // com o que vier (server.py:3934), então omitir apagaria o spot ao
          // reeditar qualquer outro campo da linha.
          spot: line.spot !== "" ? Number(line.spot) : null,
          coluna: "venda",
        });
        updatedLines.push({ ...line, dbId: saved.id });
      }
      setRecebimentoLines(updatedLines);
      setRemovedRecebimentoDbIds([]);
      toast.success("Recebimento câmbio salvo");
      load();
    } catch { toast.error("Erro ao salvar recebimento câmbio"); }
    finally { setSavingRecebimento(false); }
  };

  const saveNdfLines = async () => {
    setSavingNdf(true);
    try {
      for (const dbId of removedDbIds) await api.delete(`/posicao-dia/${dbId}`);
      const updatedLines = [];
      for (const line of ndfLines) {
        const isVenda = line.op === "VENDA";
        const valor = line.valor !== "" ? Number(line.valor) : null;
        const { data: saved } = await api.post("/posicao-dia", {
          id: line.dbId || undefined,
          data: selectedDate,
          operacao: isVenda ? "ndf_nova_venda" : "ndf_nova_compra",
          montante_usd: valor != null ? (isVenda ? -valor : valor) : null,
          taxa_venda: isVenda && line.taxa_termo !== "" ? Number(line.taxa_termo) : null,
          taxa_compra: !isVenda && line.taxa_termo !== "" ? Number(line.taxa_termo) : null,
          coluna: isVenda ? "venda" : "compra",
          spot: line.spot !== "" ? Number(line.spot) : null,
          taxa_termo: line.taxa_termo !== "" ? Number(line.taxa_termo) : null,
          data_vencimento: line.vencimento || null,
        });
        updatedLines.push({ ...line, dbId: saved.id });
      }
      setNdfLines(updatedLines);
      setRemovedDbIds([]);
      toast.success("Calculadora NDF salva");
      load();
    } catch { toast.error("Erro ao salvar calculadora NDF"); }
    finally { setSavingNdf(false); }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" style={{ maxWidth: "1440px" }}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Hedge · Posição</p>
            <h1 className="text-3xl font-bold tracking-tight">Posição do Dia</h1>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-[#6b89a8] uppercase tracking-wider">Data</label>
            <input type="date" value={selectedDate}
              onChange={(e) => { setSelectedDate(e.target.value); setEditingId(null); }}
              data-testid="input-posicao-data"
              className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]" />
            <button
              onClick={() => setCalcOpen(true)}
              data-testid="btn-calculadora-financeira"
              title="Calculadora Financeira"
              className="p-2 rounded-sm border border-[#123a5e] hover:border-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors"
            >
              <Calculator size={24} color="#40B6A2" />
            </button>
            {!readOnly && (
              <button
                onClick={() => { setImportFile(null); setImportOpen(true); }}
                data-testid="btn-importar-ndf"
                title="Importar NDF"
                className="flex items-center gap-2 px-3 py-2 rounded-sm border border-[#123a5e] hover:border-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors text-xs text-[#8aa9c9] hover:text-white"
              >
                <FileArrowUp size={18} color="#40B6A2" />
                Importar NDF
              </button>
            )}
            <button
              onClick={() => setExportOpen(true)}
              data-testid="btn-exportar-xlsx"
              title="Exportar XLSX"
              className="flex items-center gap-2 px-3 py-2 rounded-sm border border-[#123a5e] hover:border-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors text-xs text-[#8aa9c9] hover:text-white"
            >
              <FileXls size={18} color="#40B6A2" />
              Exportar XLSX
            </button>
          </div>
        </div>

        {/* Taxa das linhas TRAVA MESA, PAGAMENTO — INVOICE e RECEBIMENTO CÂMBIO: spot vs taxa negociada/taxa_final, ambos ponderados por volume */}
        <div className="flex justify-end -mt-3">
          <label
            className="flex items-center gap-2 cursor-pointer select-none"
            title="Spot ponderado por volume (soma(volume × spot) ÷ soma(volume)) nas linhas TRAVA MESA, PAGAMENTO — INVOICE e RECEBIMENTO CÂMBIO"
          >
            <span className="text-[10px] uppercase tracking-wider text-[#6b89a8]">Trava Mesa</span>
            <Switch
              checked={travaMesaSimples}
              onCheckedChange={toggleTravaMesaSimples}
              className="data-[state=checked]:bg-[#40B6A2] data-[state=unchecked]:bg-[#1e3a55]"
              data-testid="switch-trava-mesa-simples"
            />
            <span
              className="text-[10px] uppercase tracking-wider font-semibold"
              style={{ color: travaMesaSimples ? "#40B6A2" : "#8aa9c9" }}
            >
              {travaMesaSimples ? "Spot" : "Ponderada"}
            </span>
          </label>
        </div>

        {/* Modal de exportação XLSX */}
        {exportOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-[#0B2D4D] border border-[#123a5e] rounded-sm p-6 w-full max-w-sm space-y-5 shadow-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">Exportar Posição do Dia</h2>
                <button onClick={() => setExportOpen(false)} className="text-[#6b89a8] hover:text-white">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs text-[#8aa9c9] uppercase tracking-wider">Data início</label>
                  <input
                    type="date"
                    value={exportInicio}
                    onChange={e => setExportInicio(e.target.value)}
                    className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-[#8aa9c9] uppercase tracking-wider">Data fim</label>
                  <input
                    type="date"
                    value={exportFim}
                    onChange={e => setExportFim(e.target.value)}
                    className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleExportar}
                  disabled={exportLoading}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#40B6A2] text-black font-semibold py-2 rounded-sm hover:bg-[#359a89] transition-colors disabled:opacity-50"
                >
                  <FileXls size={16} />
                  {exportLoading ? "Gerando..." : "Baixar"}
                </button>
                <button
                  onClick={() => setExportOpen(false)}
                  className="flex-1 py-2 rounded-sm border border-[#123a5e] text-[#8aa9c9] hover:text-white hover:border-[#8aa9c9] transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de importação de NDF */}
        {importOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-[#0B2D4D] border border-[#123a5e] rounded-sm p-6 w-full max-w-sm space-y-5 shadow-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">Importar NDF</h2>
                <button onClick={() => setImportOpen(false)} className="text-[#6b89a8] hover:text-white">
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs text-[#8aa9c9] uppercase tracking-wider">Planilha (.xlsx)</label>
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    data-testid="input-importar-ndf-arquivo"
                    className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] file:mr-3 file:py-1 file:px-2 file:rounded-sm file:border-0 file:bg-[#40B6A2]/20 file:text-[#40B6A2] file:text-xs"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleImportarNdf}
                  disabled={importLoading}
                  data-testid="btn-confirmar-importar-ndf"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#40B6A2] text-black font-semibold py-2 rounded-sm hover:bg-[#359a89] transition-colors disabled:opacity-50"
                >
                  <FileArrowUp size={16} />
                  {importLoading ? "Importando..." : "Importar"}
                </button>
                <button
                  onClick={() => setImportOpen(false)}
                  className="flex-1 py-2 rounded-sm border border-[#123a5e] text-[#8aa9c9] hover:text-white hover:border-[#8aa9c9] transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}


        {dados?.ptax_fallback_data && (
          <div className="flex items-center gap-2 border border-[#FFB800]/30 bg-[#FFB800]/5 rounded-sm px-4 py-3 text-xs text-[#FFB800]">
            <ClockCountdown weight="fill" className="w-4 h-4 flex-shrink-0" />
            <span>Usando PTAX de {fmtDateBr(dados.ptax_fallback_data)} — nenhuma cotação BCB publicada para esta data ainda</span>
          </div>
        )}
        {aguardandoEfetivo && !dados?.ptax_fallback_data && (
          <div className="flex items-center gap-2 border border-[#FFB800]/30 bg-[#FFB800]/5 rounded-sm px-4 py-3 text-xs text-[#FFB800]">
            <ClockCountdown weight="fill" className="w-4 h-4 flex-shrink-0" />
            <span>
              {new Date().getHours() < 10
                ? "Aguardando BCB — primeira cotação publicada após 10h (horário de Brasília)"
                : "Nenhuma cotação BCB disponível — verifique se é feriado ou aguarde a próxima publicação"}
            </span>
          </div>
        )}

        {/* Two-column layout */}
        <div className="flex gap-6 items-start">

          {/* LEFT — Tabela principal (65%) */}
          <div className="space-y-4" style={{ width: "65%", minWidth: 0 }}>
            <div className="border border-[#123a5e] rounded-sm overflow-hidden">
              <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Posição em USD · {fmtDateBr(selectedDate)}</h2>
                {loading
                  ? <span className="text-xs text-[#6b89a8] animate-pulse font-mono-data">Atualizando...</span>
                  : lastUpdate && (
                    <span className="text-xs text-[#6b89a8] font-mono-data">
                      Atualizado às {lastUpdate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                    </span>
                  )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#061729] border-b border-[#123a5e]">
                    <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                      <th className="p-3 min-w-[200px]">Operação</th>
                      <th className="p-3 text-right">Montante USD</th>
                      <th className="p-3 text-right">Taxa Venda</th>
                      <th className="p-3 text-right">Taxa Compra</th>
                      <th className="p-3 text-right">Total BRL</th>
                      <th className="p-3 w-20 text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isEditing = editingId === row.opId;
                      const effColuna = (row.flexColuna && isEditing) ? editValues.coluna : row.coluna;
                      const totalBrl = getTotalBrl(row);
                      const montIsNeg = (row.montante_usd ?? 0) < 0;
                      const montColor = row.montante_usd == null ? "#6b89a8" : montIsNeg ? "#FF3B30" : "#00FF66";
                      return (
                        <tr key={row.opId} data-testid={`posicao-row-${row.opId}`}
                          className={`border-b border-[#0e2e4e] transition-colors ${isEditing ? "bg-[#0B2D4D]" : "hover:bg-[#0B2D4D]/40"}`}>

                          <td className="p-3">
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-semibold text-sm">{row.label}</span>
                                {(row.opId === "trava_mesa" || row.opId === "trava_ptax") && row.quantidade_travas > 0 && (
                                  <span className="text-[10px] text-[#6b89a8]">{row.quantidade_travas} trava{row.quantidade_travas > 1 ? "s" : ""}</span>
                                )}
                              </div>
                              {isEditing && row.flexColuna && (
                                <div className="flex gap-4 pt-1">
                                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input type="radio" className="accent-[#40B6A2]" checked={editValues.coluna === "venda"}
                                      onChange={() => setEditValues((v) => ({ ...v, coluna: "venda", taxa_compra: "" }))} />
                                    <span>Venda</span>
                                  </label>
                                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input type="radio" className="accent-[#40B6A2]" checked={editValues.coluna === "compra"}
                                      onChange={() => setEditValues((v) => ({ ...v, coluna: "compra", taxa_venda: "" }))} />
                                    <span>Compra</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="p-3 text-right font-mono-data">
                            {isEditing && !row.auto && row.editMontante ? (
                              <input type="text" inputMode="decimal" value={formatarValorBR(editValues.montante_usd)}
                                onChange={(e) => setEditValues((v) => ({ ...v, montante_usd: parsearValorBR(e.target.value) }))}
                                onPaste={colarValor((canonico) => setEditValues((v) => ({ ...v, montante_usd: canonico })))}
                                data-testid={`input-montante-${row.opId}`}
                                className={numInput} placeholder="0" />
                            ) : (
                              <span style={{ color: montColor }}>{row.montante_usd != null ? fmt2(row.montante_usd) : "—"}</span>
                            )}
                          </td>

                          <td className="p-3 text-right font-mono-data">
                            {isEditing && !row.auto && row.editTaxa && !row.ptaxField && effColuna === "venda" ? (
                              <input type="number" step="0.0001" value={editValues.taxa_venda}
                                onChange={(e) => setEditValues((v) => ({ ...v, taxa_venda: e.target.value }))}
                                data-testid={`input-taxa-venda-${row.opId}`}
                                className={numInput} placeholder="0,0000" />
                            ) : (
                              <span className={effColuna === "venda" ? "text-white" : "text-[#1a3a5c]"}>
                                {effColuna !== "venda" ? "—"
                                  : row.taxa_venda != null
                                    ? <span>
                                        <span className="block">{`R$ ${fmt4(row.taxa_venda)}`}</span>
                                        {row.ptaxField && (
                                          <span className="text-[10px] font-normal" style={{ color: dados?.ptax_fallback_data ? "#FFB800" : dados?.ptax_parcial ? "#FFB800" : "#6b89a8" }}>
                                            {dados?.ptax_fallback_data
                                              ? `ref. ${fmtDateBr(dados.ptax_fallback_data)}`
                                              : dados?.ptax_parcial && dados?.ptax_hora
                                                ? `PTAX parcial · ${dados.ptax_hora.slice(0, 5)}`
                                                : dados?.ptax_hora
                                                  ? `PTAX fechamento · ${dados.ptax_hora.slice(0, 5)}`
                                                  : null}
                                          </span>
                                        )}
                                      </span>
                                    : aguardandoEfetivo
                                      ? <span className="text-[#FFB800] text-xs italic">
                                          {new Date().getHours() < 10 ? "Aguardando BCB (publica após 10h)" : "Sem dados BCB"}
                                        </span>
                                      : "—"}
                              </span>
                            )}
                          </td>

                          <td className="p-3 text-right font-mono-data">
                            {isEditing && !row.auto && row.editTaxa && !row.ptaxField && effColuna === "compra" ? (
                              <input type="number" step="0.0001" value={editValues.taxa_compra}
                                onChange={(e) => setEditValues((v) => ({ ...v, taxa_compra: e.target.value }))}
                                data-testid={`input-taxa-compra-${row.opId}`}
                                className={numInput} placeholder="0,0000" />
                            ) : (
                              <span className={effColuna === "compra" ? "text-white" : "text-[#1a3a5c]"}>
                                {effColuna !== "compra" ? "—"
                                  : row.opId === "trava_ptax"
                                    ? (row.taxa_compra != null
                                        ? <span>
                                            <span className="block">{`R$ ${fmt4(row.taxa_compra)}`}</span>
                                            <span className="text-[10px] font-normal" style={{ color: "#6b89a8" }} data-testid="rotulo-trava-ptax">
                                              {rotuloTravaPtax(row)}
                                            </span>
                                          </span>
                                        : row.ptax_indisponivel
                                          ? <span className="text-[#FFB800] text-xs italic" data-testid="rotulo-trava-ptax">{rotuloTravaPtax(row)}</span>
                                          : "—")
                                  : row.taxa_compra != null
                                    ? <span>
                                        <span className="block">{`R$ ${fmt4(row.taxa_compra)}`}</span>
                                        {row.ptaxField && (
                                          <span className="text-[10px] font-normal" style={{ color: dados?.ptax_fallback_data ? "#FFB800" : dados?.ptax_parcial ? "#FFB800" : "#6b89a8" }}>
                                            {dados?.ptax_fallback_data
                                              ? `ref. ${fmtDateBr(dados.ptax_fallback_data)}`
                                              : dados?.ptax_parcial && dados?.ptax_hora
                                                ? `PTAX parcial · ${dados.ptax_hora.slice(0, 5)}`
                                                : dados?.ptax_hora
                                                  ? `PTAX fechamento · ${dados.ptax_hora.slice(0, 5)}`
                                                  : null}
                                          </span>
                                        )}
                                      </span>
                                    : aguardandoEfetivo
                                      ? <span className="text-[#FFB800] text-xs italic">
                                          {new Date().getHours() < 10 ? "Aguardando BCB (publica após 10h)" : "Sem dados BCB"}
                                        </span>
                                      : "—"}
                              </span>
                            )}
                          </td>

                          <td className="p-3 text-right font-mono-data">
                            {totalBrl != null ? (
                              <span style={{ color: totalBrl < 0 ? "#FF3B30" : "#00FF66" }}>R$ {fmt2(totalBrl)}</span>
                            ) : <span className="text-[#1a3a5c]">—</span>}
                          </td>

                          <td className="p-3">
                            {!readOnly && !row.auto && !row.fromCalculator && (
                              <div className="flex items-center gap-1 justify-center">
                                {isEditing ? (
                                  <>
                                    <button onClick={() => saveRow(row)} disabled={saving} title="Salvar"
                                      data-testid={`btn-salvar-${row.opId}`}
                                      className="p-1.5 rounded-sm bg-[#40B6A2]/10 text-[#40B6A2] hover:bg-[#40B6A2]/20 transition-colors disabled:opacity-40">
                                      <FloppyDisk className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setEditingId(null)} title="Cancelar"
                                      className="p-1.5 rounded-sm bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors">
                                      <X className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <div className="flex gap-1">
                                    <button onClick={() => startEdit(row)} title="Editar"
                                      data-testid={`btn-editar-${row.opId}`}
                                      className="p-1.5 rounded-sm text-[#6b89a8] hover:text-white hover:bg-[#0B2D4D] transition-colors">
                                      <PencilSimple className="w-4 h-4" />
                                    </button>
                                    {row.dbId && (
                                      <button onClick={() => clearRow(row)} title="Limpar lançamento"
                                        data-testid={`btn-limpar-${row.opId}`}
                                        className="p-1.5 rounded-sm text-[#6b89a8] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {/* AJUSTE MANUAL */}
                    <tr key="ajuste_manual" data-testid="posicao-row-ajuste_manual"
                      className={`border-b border-[#0e2e4e] transition-colors ${editingAjuste ? "bg-[#0B2D4D]" : "hover:bg-[#0B2D4D]/40"}`}>
                      <td className="p-3">
                        <div className="space-y-1">
                          <span className="font-semibold text-sm">MOVIMENTAÇÃO ABERTURA</span>
                          {editingAjuste && (
                            <div className="flex gap-4 pt-1">
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="radio" className="accent-[#40B6A2]" checked={ajusteColuna === "venda"}
                                  onChange={() => setAjusteColuna("venda")} />
                                <span>Venda</span>
                              </label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="radio" className="accent-[#40B6A2]" checked={ajusteColuna === "compra"}
                                  onChange={() => setAjusteColuna("compra")} />
                                <span>Compra</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {editingAjuste ? (
                          <input type="text" inputMode="decimal" value={formatarValorBR(ajusteValor)}
                            onChange={(e) => setAjusteValor(parsearValorBR(e.target.value))}
                            onPaste={colarValor(setAjusteValor)}
                            placeholder="0" className={numInput} />
                        ) : (
                          <span style={{ color: ajuste?.montante_usd == null ? "#6b89a8" : ajuste.montante_usd < 0 ? "#FF3B30" : "#00FF66" }}>
                            {ajuste?.montante_usd != null ? fmt2(ajuste.montante_usd) : "—"}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {effAjusteColuna === "venda" && ptaxAjuste != null ? (
                          <span className="text-white">
                            <span className="block">{`R$ ${fmt4(ptaxAjuste)}`}</span>
                            <span className="text-[10px] font-normal" style={{ color: ajusteTaxaManual != null ? "#6b89a8" : dados?.ptax_d1?.ptax_fallback_data ? "#FFB800" : dados?.ptax_d1?.ptax_parcial ? "#FFB800" : "#6b89a8" }}>
                              {ajusteTaxaManual != null
                                ? "taxa manual"
                                : dados?.ptax_d1?.ptax_fallback_data
                                ? `ref. ${fmtDateBr(dados.ptax_d1.ptax_fallback_data)}`
                                : dados?.ptax_d1?.ptax_parcial && dados?.ptax_d1?.ptax_hora
                                  ? `PTAX parcial · ${dados.ptax_d1.ptax_hora.slice(0, 5)}`
                                  : dados?.ptax_d1?.ptax_hora
                                    ? `PTAX fechamento · ${dados.ptax_d1.ptax_hora.slice(0, 5)}`
                                    : null}
                            </span>
                          </span>
                        ) : <span className="text-[#1a3a5c]">—</span>}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {effAjusteColuna === "compra" && ptaxAjuste != null ? (
                          <span className="text-white">
                            <span className="block">{`R$ ${fmt4(ptaxAjuste)}`}</span>
                            <span className="text-[10px] font-normal" style={{ color: ajusteTaxaManual != null ? "#6b89a8" : dados?.ptax_d1?.ptax_fallback_data ? "#FFB800" : dados?.ptax_d1?.ptax_parcial ? "#FFB800" : "#6b89a8" }}>
                              {ajusteTaxaManual != null
                                ? "taxa manual"
                                : dados?.ptax_d1?.ptax_fallback_data
                                ? `ref. ${fmtDateBr(dados.ptax_d1.ptax_fallback_data)}`
                                : dados?.ptax_d1?.ptax_parcial && dados?.ptax_d1?.ptax_hora
                                  ? `PTAX parcial · ${dados.ptax_d1.ptax_hora.slice(0, 5)}`
                                  : dados?.ptax_d1?.ptax_hora
                                    ? `PTAX fechamento · ${dados.ptax_d1.ptax_hora.slice(0, 5)}`
                                    : null}
                            </span>
                          </span>
                        ) : <span className="text-[#1a3a5c]">—</span>}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {(() => {
                          const ajTotalBrl = ajuste?.montante_usd != null && ptaxAjuste != null
                            ? ajuste.montante_usd * ptaxAjuste : null;
                          return ajTotalBrl != null
                            ? <span style={{ color: ajTotalBrl < 0 ? "#FF3B30" : "#00FF66" }}>R$ {fmt2(ajTotalBrl)}</span>
                            : <span className="text-[#1a3a5c]">—</span>;
                        })()}
                      </td>
                      <td className="p-3">
                        {!readOnly && (
                          <div className="flex items-center gap-1 justify-center">
                            {editingAjuste ? (
                              <>
                                <button onClick={saveAjuste} disabled={savingAjuste} title="Salvar"
                                  className="p-1.5 rounded-sm bg-[#40B6A2]/10 text-[#40B6A2] hover:bg-[#40B6A2]/20 transition-colors disabled:opacity-40">
                                  <FloppyDisk className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditingAjuste(false)} title="Cancelar"
                                  className="p-1.5 rounded-sm bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors">
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => { setEditingAjuste(true); setAjusteValor(ajuste?.montante_usd != null ? String(Math.abs(ajuste.montante_usd)) : ""); setAjusteColuna(ajuste?.coluna ?? "venda"); }}
                                  title="Editar ajuste" className="p-1.5 rounded-sm text-[#6b89a8] hover:text-white hover:bg-[#0B2D4D] transition-colors">
                                  <PencilSimple className="w-4 h-4" />
                                </button>
                                {ajuste?.id && (
                                  <button onClick={clearAjuste} title="Remover ajuste"
                                    className="p-1.5 rounded-sm text-[#6b89a8] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* AJUSTE MANUAL 2 */}
                    <tr key="ajuste_manual_2" data-testid="posicao-row-ajuste_manual_2"
                      className={`border-b border-[#0e2e4e] transition-colors ${editingAjuste2 ? "bg-[#0B2D4D]" : "hover:bg-[#0B2D4D]/40"}`}>
                      <td className="p-3">
                        <div className="space-y-1">
                          <span className="font-semibold text-sm">MOVIMENTAÇÃO FECHAMENTO</span>
                          {editingAjuste2 && (
                            <div className="flex gap-4 pt-1">
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="radio" className="accent-[#40B6A2]" checked={ajuste2Coluna === "venda"}
                                  onChange={() => setAjuste2Coluna("venda")} />
                                <span>Venda</span>
                              </label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input type="radio" className="accent-[#40B6A2]" checked={ajuste2Coluna === "compra"}
                                  onChange={() => setAjuste2Coluna("compra")} />
                                <span>Compra</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {editingAjuste2 ? (
                          <input type="text" inputMode="decimal" value={formatarValorBR(ajuste2Valor)}
                            onChange={(e) => setAjuste2Valor(parsearValorBR(e.target.value))}
                            onPaste={colarValor(setAjuste2Valor)}
                            placeholder="0" className={numInput} />
                        ) : (
                          <span style={{ color: ajuste2?.montante_usd == null ? "#6b89a8" : ajuste2.montante_usd < 0 ? "#FF3B30" : "#00FF66" }}>
                            {ajuste2?.montante_usd != null ? fmt2(ajuste2.montante_usd) : "—"}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {effAjuste2Coluna === "venda" && ptaxFech != null ? (
                          <span className="text-white">
                            <span className="block">{`R$ ${fmt4(ptaxFech)}`}</span>
                            <span className="text-[10px] font-normal" style={{ color: dados?.ptax_fallback_data ? "#FFB800" : dados?.ptax_parcial ? "#FFB800" : "#6b89a8" }}>
                              {dados?.ptax_fallback_data
                                ? `ref. ${fmtDateBr(dados.ptax_fallback_data)}`
                                : dados?.ptax_parcial && dados?.ptax_hora
                                  ? `PTAX parcial · ${dados.ptax_hora.slice(0, 5)}`
                                  : dados?.ptax_hora
                                    ? `PTAX fechamento · ${dados.ptax_hora.slice(0, 5)}`
                                    : null}
                            </span>
                          </span>
                        ) : <span className="text-[#1a3a5c]">—</span>}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {effAjuste2Coluna === "compra" && ptaxFech != null ? (
                          <span className="text-white">
                            <span className="block">{`R$ ${fmt4(ptaxFech)}`}</span>
                            <span className="text-[10px] font-normal" style={{ color: dados?.ptax_fallback_data ? "#FFB800" : dados?.ptax_parcial ? "#FFB800" : "#6b89a8" }}>
                              {dados?.ptax_fallback_data
                                ? `ref. ${fmtDateBr(dados.ptax_fallback_data)}`
                                : dados?.ptax_parcial && dados?.ptax_hora
                                  ? `PTAX parcial · ${dados.ptax_hora.slice(0, 5)}`
                                  : dados?.ptax_hora
                                    ? `PTAX fechamento · ${dados.ptax_hora.slice(0, 5)}`
                                    : null}
                            </span>
                          </span>
                        ) : <span className="text-[#1a3a5c]">—</span>}
                      </td>
                      <td className="p-3 text-right font-mono-data">
                        {(() => {
                          const aj2TotalBrl = ajuste2?.montante_usd != null && ptaxFech != null
                            ? ajuste2.montante_usd * ptaxFech : null;
                          return aj2TotalBrl != null
                            ? <span style={{ color: aj2TotalBrl < 0 ? "#FF3B30" : "#00FF66" }}>R$ {fmt2(aj2TotalBrl)}</span>
                            : <span className="text-[#1a3a5c]">—</span>;
                        })()}
                      </td>
                      <td className="p-3">
                        {!readOnly && (
                          <div className="flex items-center gap-1 justify-center">
                            {editingAjuste2 ? (
                              <>
                                <button onClick={saveAjuste2} disabled={savingAjuste2} title="Salvar"
                                  className="p-1.5 rounded-sm bg-[#40B6A2]/10 text-[#40B6A2] hover:bg-[#40B6A2]/20 transition-colors disabled:opacity-40">
                                  <FloppyDisk className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditingAjuste2(false)} title="Cancelar"
                                  className="p-1.5 rounded-sm bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-colors">
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => { setEditingAjuste2(true); setAjuste2Valor(ajuste2?.montante_usd != null ? String(Math.abs(ajuste2.montante_usd)) : ""); setAjuste2Coluna(ajuste2?.coluna ?? "venda"); }}
                                  title="Editar ajuste" className="p-1.5 rounded-sm text-[#6b89a8] hover:text-white hover:bg-[#0B2D4D] transition-colors">
                                  <PencilSimple className="w-4 h-4" />
                                </button>
                                {ajuste2?.id && (
                                  <button onClick={clearAjuste2} title="Remover ajuste"
                                    className="p-1.5 rounded-sm text-[#6b89a8] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* EXPOSIÇÃO DO DIA */}
                    {(() => {
                      const corExposicao = totalMontante < 0 ? '#FF3B30' : '#40B6A2';
                      return (
                        <tr className="border-t-2 font-bold"
                          style={{ borderColor: corExposicao, background: `${corExposicao}0d` }}
                          data-testid="posicao-row-total">
                          <td className="p-4 text-sm tracking-[0.15em] uppercase">Exposição do Dia</td>
                          <td className="p-4 text-right font-mono-data text-lg" style={{ color: corExposicao }}>
                            {fmt2(totalMontante)}
                          </td>
                          <td colSpan={4}></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* TRAVA PTAX sem PTAX compra D-1: o montante segue na exposição,
                mas fica fora da Ponderada Compra e do Resultado. */}
            {dados?.trava_ptax?.ptax_indisponivel && (
              <div className="border border-[#FFB800]/40 rounded-sm px-4 py-2 bg-[#FFB800]/10 text-xs text-[#FFB800]" data-testid="aviso-trava-ptax-indisponivel">
                PTAX compra D-1 indisponível: a Ponderada Compra e o Resultado estão calculados SEM a TRAVA PTAX
                ({fmt2(dados.trava_ptax.montante_usd)} USD).
              </div>
            )}

            {/* Ponderada Venda | Resultado | Ponderada Compra */}
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>

              <div className="border border-[#FF3B30]/30 rounded-sm p-5 bg-[#0B2D4D]" style={{ flex: 1 }} data-testid="card-ponderada-venda">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#6b89a8] mb-3">Ponderada Venda</p>
                <p className="text-2xl font-bold font-mono-data" style={{ color: "#FF3B30" }}>
                  {pondVenda != null ? `R$ ${fmt4(pondVenda)}` : "—"}
                </p>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-[#6b89a8] font-mono-data">Total USD vendido: {fmt2(somaVV)}</p>
                  <p className="text-xs text-[#6b89a8] font-mono-data">Total BRL: {somaVV > 0 ? `R$ ${fmt2(somaBV)}` : "—"}</p>
                </div>
              </div>

              <div
                data-testid="card-resultado-ponderada"
                className="border-2 rounded-sm p-6 text-center"
                style={{
                  flex: 1,
                  borderColor: resultadoPond == null ? "#123a5e" : resultadoPond >= 0 ? "#40B6A2" : "#FF3B30",
                  background: resultadoPond == null ? "#0B2D4D" : resultadoPond >= 0 ? "rgba(64,182,162,0.10)" : "rgba(255,59,48,0.10)",
                }}
              >
                <p
                  className="text-[10px] tracking-[0.35em] uppercase mb-3"
                  style={{ color: resultadoPond == null ? "#6b89a8" : resultadoPond >= 0 ? "#40B6A2" : "#FF3B30" }}
                >
                  Resultado
                </p>
                <p
                  className="font-bold font-mono-data leading-none"
                  style={{
                    fontSize: "3rem",
                    color: resultadoPond == null ? "#6b89a8" : resultadoPond >= 0 ? "#40B6A2" : "#FF3B30",
                    textShadow: resultadoPond != null ? `0 0 32px ${resultadoPond >= 0 ? "rgba(64,182,162,0.5)" : "rgba(255,59,48,0.5)"}` : "none",
                  }}
                >
                  {resultadoPond != null
                    ? `${(resultadoPond * 100).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}%`
                    : "—"}
                </p>
              </div>

              <div className="border border-blue-500/30 rounded-sm p-5 bg-[#0B2D4D]" style={{ flex: 1 }} data-testid="card-ponderada-compra">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#6b89a8] mb-3">Ponderada Compra</p>
                <p className="text-2xl font-bold font-mono-data" style={{ color: "#00FF66" }}>
                  {pondCompra != null ? `R$ ${fmt4(pondCompra)}` : "—"}
                </p>
                <div className="mt-3 space-y-1">
                  <p className="text-xs text-[#6b89a8] font-mono-data">Total USD comprado: {fmt2(somaVC)}</p>
                  <p className="text-xs text-[#6b89a8] font-mono-data">Total BRL: {somaVC > 0 ? `R$ ${fmt2(somaBC)}` : "—"}</p>
                </div>
              </div>

            </div>

            {/* PTAX BACEN — abaixo das ponderadas na coluna esquerda */}
            {ptaxCotacoes.length > 0 && (
              <div className="border border-[#123a5e] rounded-sm overflow-hidden" data-testid="painel-ptax-cotacoes">
                <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
                  <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">PTAX BACEN · Cotações do Dia</h2>
                  <span className="text-xs text-[#6b89a8] font-mono-data">
                    {ptaxCotacoes.length} publicação{ptaxCotacoes.length > 1 ? "ões" : ""} · atualiza a cada 15min
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#061729] border-b border-[#123a5e]">
                      <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                        <th className="p-3">Horário</th>
                        <th className="p-3">Tipo</th>
                        <th className="p-3 text-right">Compra</th>
                        <th className="p-3 text-right">Venda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ptaxCotacoes.map((c, idx) => {
                        const isLast = idx === ptaxCotacoes.length - 1;
                        const tb = c.tipo_boletim || '';
                        const isFechamento = tb.toLowerCase().includes('fechamento');
                        const tipo = tb.toLowerCase().includes('abertura')
                          ? { label: 'Abertura', cor: '#3b82f6' }
                          : isFechamento
                            ? { label: 'Fech. PTAX', cor: '#f59e0b' }
                            : { label: 'Intermediário', cor: '#64748b' };
                        return (
                          <tr key={c.id ?? `${c.hora}-${c.tipo_boletim}`} data-testid={`ptax-cotacao-row-${idx}`}
                            className="border-b border-[#0e2e4e] transition-colors"
                            style={isFechamento ? { background: 'rgba(245,158,11,0.06)' } : undefined}>
                            <td className="p-3 font-mono-data text-sm">
                              <div className="flex items-center gap-2">
                                {c.hora}
                                {isLast && (
                                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold"
                                    style={isFechamento ? { background: '#f59e0b', color: '#000' } : { background: '#FFB800', color: '#000' }}>
                                    {isFechamento ? 'Fechamento' : 'Atual'}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-medium"
                                style={{ color: tipo.cor, border: `1px solid ${tipo.cor}40`, background: `${tipo.cor}12` }}>
                                {tipo.label}
                              </span>
                            </td>
                            <td className="p-3 text-right font-mono-data" style={{ color: "#00FF66" }}>
                              {c.cotacao_compra != null ? `R$ ${fmt4(c.cotacao_compra)}` : "—"}
                            </td>
                            <td className="p-3 text-right font-mono-data" style={{ color: "#FF3B30" }}>
                              {c.cotacao_venda != null ? `R$ ${fmt4(c.cotacao_venda)}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — Calculadora NDF (35%) */}
          <div className="space-y-4" style={{ width: "35%", minWidth: 0 }}>
            <div className="border border-[#123a5e] rounded-sm overflow-hidden">
              <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Calculadora NDF</h2>
                {!readOnly && (
                  <button onClick={addNdfLine} data-testid="btn-add-ndf-line"
                    className="flex items-center gap-1 px-2.5 py-1 text-xs border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors rounded-sm">
                    <Plus className="w-3.5 h-3.5" />
                    <span>Linha</span>
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#061729] border-b border-[#123a5e]">
                    <tr className="text-[9px] tracking-[0.15em] uppercase text-[#6b89a8]">
                      <th className="px-2 py-2 text-left w-[72px]">OP</th>
                      <th className="px-2 py-2 text-right" style={{ minWidth: "90px" }}>Valor USD</th>
                      <th className="px-2 py-2 text-right">Spot</th>
                      <th className="px-2 py-2 text-right">Tx Termo</th>
                      <th className="px-2 py-2 text-right">Vencto</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ndfLines.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-[#6b89a8] text-xs">
                          Nenhuma linha. Clique em + Linha para adicionar.
                        </td>
                      </tr>
                    )}
                    {ndfLines.map((line, idx) => (
                      <tr key={line.localId} data-testid={`ndf-line-${idx}`}
                        className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/40 transition-colors">
                        <td className="px-2 py-1.5" style={{ minWidth: "90px" }}>
                          <select value={line.op}
                            onChange={(e) => updateNdfLine(line.localId, "op", e.target.value)}
                            data-testid={`ndf-op-${idx}`}
                            className="w-full bg-[#061729] border border-[#1e3a55] rounded-sm px-1.5 py-1 text-xs font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]">
                            <option value="VENDA">VENDA</option>
                            <option value="COMPRA">COMPRA</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5" style={{ minWidth: "120px" }}>
                          <input type="text" inputMode="decimal" value={formatarValorBR(line.valor)}
                            onChange={(e) => updateNdfLine(line.localId, "valor", parsearValorBR(e.target.value))}
                            onPaste={colarValor((canonico) => updateNdfLine(line.localId, "valor", canonico))}
                            data-testid={`ndf-valor-${idx}`} placeholder="200.000"
                            className={calcInput} />
                        </td>
                        <td className="px-2 py-1.5" style={{ minWidth: "90px" }}>
                          <input type="number" step="0.0001" value={line.spot}
                            onChange={(e) => updateNdfLine(line.localId, "spot", e.target.value)}
                            data-testid={`ndf-spot-${idx}`} placeholder="4.9300"
                            className={calcInput} />
                        </td>
                        <td className="px-2 py-1.5" style={{ minWidth: "90px" }}>
                          <input type="number" step="0.0001" value={line.taxa_termo}
                            onChange={(e) => updateNdfLine(line.localId, "taxa_termo", e.target.value)}
                            data-testid={`ndf-taxa-termo-${idx}`} placeholder="4.9300"
                            className={calcInput} />
                        </td>
                        <td className="px-2 py-1.5" style={{ minWidth: "150px" }}>
                          <input type="date" value={line.vencimento}
                            onChange={(e) => updateNdfLine(line.localId, "vencimento", e.target.value)}
                            data-testid={`ndf-vencimento-${idx}`}
                            className="w-full bg-[#061729] border border-[#1e3a55] rounded-sm px-1 py-1 text-xs font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <button onClick={() => removeNdfLine(line.localId)}
                            data-testid={`ndf-remove-${idx}`}
                            className="p-1 text-[#6b89a8] hover:text-[#FF3B30] transition-colors" title="Remover">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!readOnly && (
                <div className="p-3 border-t border-[#123a5e] bg-[#061729] flex justify-end">
                  <button onClick={saveNdfLines} disabled={savingNdf} data-testid="btn-salvar-ndf"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#40B6A2]/10 border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/20 transition-colors rounded-sm disabled:opacity-40">
                    <FloppyDisk className="w-3.5 h-3.5" />
                    {savingNdf ? "Salvando..." : "Salvar NDF"}
                  </button>
                </div>
              )}
            </div>

            {/* Calculadora Invoice */}
            <div className="border border-[#123a5e] rounded-sm overflow-hidden">
              <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Pagamento Invoice</h2>
                {!readOnly && (
                  <button onClick={addInvoiceLine} data-testid="btn-add-invoice-line"
                    className="flex items-center gap-1 px-2.5 py-1 text-xs border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors rounded-sm">
                    <Plus className="w-3.5 h-3.5" /><span>Linha</span>
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#061729] border-b border-[#123a5e]">
                    <tr className="text-[9px] tracking-[0.15em] uppercase text-[#6b89a8]">
                      <th className="px-2 py-2 text-right">Valor USD</th>
                      <th className="px-2 py-2 text-right" style={{ minWidth: "72px" }}>Taxa</th>
                      <th className="px-2 py-2 text-right" style={{ minWidth: "72px" }}>Spot</th>
                      <th className="px-2 py-2 text-left">Banco</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceLines.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-8 text-center text-[#6b89a8] text-xs">
                          Nenhuma linha. Clique em + Linha para adicionar.
                        </td>
                      </tr>
                    )}
                    {invoiceLines.map((line, idx) => {
                      const totalBrl = line.valor_usd && line.taxa ? Number(line.valor_usd) * Number(line.taxa) : null;
                      return (
                        <tr key={line.localId} data-testid={`invoice-line-${idx}`}
                          className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/40 transition-colors">
                          <td className="px-2 py-1.5">
                            <input type="text" inputMode="decimal" value={formatarValorBR(line.valor_usd)}
                              onChange={(e) => updateInvoiceLine(line.localId, "valor_usd", parsearValorBR(e.target.value))}
                              onPaste={colarValor((canonico) => updateInvoiceLine(line.localId, "valor_usd", canonico))}
                              data-testid={`invoice-valor-${idx}`} placeholder="1.129.191,90"
                              className={calcInput} />
                          </td>
                          <td className="px-2 py-1.5" style={{ minWidth: "72px" }}>
                            <input type="number" step="0.0001" value={line.taxa}
                              onChange={(e) => updateInvoiceLine(line.localId, "taxa", e.target.value)}
                              data-testid={`invoice-taxa-${idx}`} placeholder="4.9300"
                              className={calcInput} />
                          </td>
                          <td className="px-2 py-1.5" style={{ minWidth: "72px" }}>
                            <input type="number" step="0.0001" value={line.spot}
                              onChange={(e) => updateInvoiceLine(line.localId, "spot", e.target.value)}
                              data-testid={`invoice-spot-${idx}`} placeholder="4.9200"
                              title="Spot de mercado no momento do pagamento. Em branco, a ponderada spot usa a taxa negociada."
                              className={calcInput} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input type="text" value={line.banco}
                              onChange={(e) => updateInvoiceLine(line.localId, "banco", e.target.value)}
                              data-testid={`invoice-banco-${idx}`} placeholder="Banco..."
                              className={calcInput} />
                          </td>
                          <td className="px-2 py-1.5">
                            <button onClick={() => removeInvoiceLine(line.localId)}
                              className="p-1 rounded-sm text-[#6b89a8] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {invoiceLines.length > 0 && invoiceCalc.montante != null && invoiceCalc.taxa != null && (
                      <tr className="border-t border-[#123a5e] bg-[#061729] text-[10px] text-[#8aa9c9] font-mono-data">
                        <td className="px-2 py-1.5 text-right">Total: {fmt2(invoiceCalc.soma)}</td>
                        <td className="px-2 py-1.5 text-right">R$ {fmt4(invoiceCalc.taxa)}</td>
                        <td className="px-2 py-1.5 text-right">
                          {invoiceCalc.spotPond != null ? `R$ ${fmt4(invoiceCalc.spotPond)}` : "—"}
                        </td>
                        <td colSpan={2} className="px-2 py-1.5">BRL: R$ {fmt2(invoiceCalc.soma * invoiceCalc.taxa)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!readOnly && (
                <div className="p-3 border-t border-[#123a5e] bg-[#061729] flex justify-end">
                  <button onClick={saveInvoice} disabled={savingInvoice} data-testid="btn-salvar-invoice"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#40B6A2]/10 border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/20 transition-colors rounded-sm disabled:opacity-40">
                    <FloppyDisk className="w-3.5 h-3.5" />
                    {savingInvoice ? "Salvando..." : "Salvar Invoice"}
                  </button>
                </div>
              )}
            </div>

            {/* Calculadora Recebimento Câmbio — espelha a de invoice, sem a
                coluna Banco e do lado da venda. O montante mostrado no rodapé
                já sai negativo, que é como vai para o banco. */}
            <div className="border border-[#123a5e] rounded-sm overflow-hidden">
              <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">Recebimento Câmbio</h2>
                {!readOnly && (
                  <button onClick={addRecebimentoLine} data-testid="btn-add-recebimento-line"
                    className="flex items-center gap-1 px-2.5 py-1 text-xs border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors rounded-sm">
                    <Plus className="w-3.5 h-3.5" /><span>Linha</span>
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#061729] border-b border-[#123a5e]">
                    <tr className="text-[9px] tracking-[0.15em] uppercase text-[#6b89a8]">
                      <th className="px-2 py-2 text-right">Valor USD</th>
                      <th className="px-2 py-2 text-right" style={{ minWidth: "72px" }}>Taxa</th>
                      <th className="px-2 py-2 text-right" style={{ minWidth: "72px" }}>Spot</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recebimentoLines.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-8 text-center text-[#6b89a8] text-xs">
                          Nenhuma linha. Clique em + Linha para adicionar.
                        </td>
                      </tr>
                    )}
                    {recebimentoLines.map((line, idx) => (
                      <tr key={line.localId} data-testid={`recebimento-line-${idx}`}
                        className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/40 transition-colors">
                        <td className="px-2 py-1.5">
                          <input type="text" inputMode="decimal" value={formatarValorBR(line.valor_usd)}
                            onChange={(e) => updateRecebimentoLine(line.localId, "valor_usd", parsearValorBR(e.target.value))}
                            onPaste={colarValor((canonico) => updateRecebimentoLine(line.localId, "valor_usd", canonico))}
                            data-testid={`recebimento-valor-${idx}`} placeholder="1.129.191,90"
                            title="Digite o valor positivo. A linha é sempre lançada como negativa (coluna venda)."
                            className={calcInput} />
                        </td>
                        <td className="px-2 py-1.5" style={{ minWidth: "72px" }}>
                          <input type="number" step="0.0001" value={line.taxa}
                            onChange={(e) => updateRecebimentoLine(line.localId, "taxa", e.target.value)}
                            data-testid={`recebimento-taxa-${idx}`} placeholder="4.9300"
                            className={calcInput} />
                        </td>
                        <td className="px-2 py-1.5" style={{ minWidth: "72px" }}>
                          <input type="number" step="0.0001" value={line.spot}
                            onChange={(e) => updateRecebimentoLine(line.localId, "spot", e.target.value)}
                            data-testid={`recebimento-spot-${idx}`} placeholder="4.9200"
                            title="Spot de mercado no momento do recebimento. Em branco, a ponderada spot usa a taxa negociada."
                            className={calcInput} />
                        </td>
                        <td className="px-2 py-1.5">
                          <button onClick={() => removeRecebimentoLine(line.localId)}
                            className="p-1 rounded-sm text-[#6b89a8] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {recebimentoLines.length > 0 && recebimentoCalc.montante != null && recebimentoCalc.taxa != null && (
                      <>
                        <tr className="border-t border-[#123a5e] bg-[#061729] text-[10px] text-[#8aa9c9] font-mono-data">
                          <td className="px-2 py-1.5 text-right">Total: {fmt2(recebimentoCalc.montante)}</td>
                          <td className="px-2 py-1.5 text-right">R$ {fmt4(recebimentoCalc.taxa)}</td>
                          <td className="px-2 py-1.5 text-right">
                            {recebimentoCalc.spotPond != null ? `R$ ${fmt4(recebimentoCalc.spotPond)}` : "—"}
                          </td>
                          <td className="px-2 py-1.5"></td>
                        </tr>
                        <tr className="bg-[#061729] text-[10px] text-[#8aa9c9] font-mono-data">
                          <td colSpan={4} className="px-2 py-1.5">BRL: R$ {fmt2(recebimentoCalc.montante * recebimentoCalc.taxa)}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {!readOnly && (
                <div className="p-3 border-t border-[#123a5e] bg-[#061729] flex justify-end">
                  <button onClick={saveRecebimento} disabled={savingRecebimento} data-testid="btn-salvar-recebimento"
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#40B6A2]/10 border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/20 transition-colors rounded-sm disabled:opacity-40">
                    <FloppyDisk className="w-3.5 h-3.5" />
                    {savingRecebimento ? "Salvando..." : "Salvar Recebimento Câmbio"}
                  </button>
                </div>
              )}
            </div>

            {/* Cards de ponderada NDF */}
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-[#FF3B30]/30 rounded-sm p-4 bg-[#0B2D4D]" data-testid="card-ndf-ponderada-venda">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#6b89a8] mb-2">Ponderada Venda NDF</p>
                <p className="text-lg font-bold font-mono-data" style={{ color: "#FF3B30" }}>
                  {ndfCalc.vendaTaxa != null ? `R$ ${fmt4(ndfCalc.vendaTaxa)}` : "—"}
                </p>
                <p className="text-[10px] text-[#6b89a8] font-mono-data mt-2">
                  Total: {ndfCalc.somaVenda > 0 ? `-${fmt2(ndfCalc.somaVenda)}` : "—"}
                </p>
              </div>
              <div className="border border-blue-500/30 rounded-sm p-4 bg-[#0B2D4D]" data-testid="card-ndf-ponderada-compra">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#6b89a8] mb-2">Ponderada Compra NDF</p>
                <p className="text-lg font-bold font-mono-data" style={{ color: "#00FF66" }}>
                  {ndfCalc.compraTaxa != null ? `R$ ${fmt4(ndfCalc.compraTaxa)}` : "—"}
                </p>
                <p className="text-[10px] text-[#6b89a8] font-mono-data mt-2">
                  Total: {ndfCalc.somaCompra > 0 ? `+${fmt2(ndfCalc.somaCompra)}` : "—"}
                </p>
              </div>
            </div>

            {/* Card Spot USD/BRL */}
            <div className="border border-[#40B6A2]/30 rounded-sm p-4 bg-[#0B2D4D]" data-testid="card-spot-usd-brl">
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#6b89a8] mb-2">USD/BRL SPOT</p>
              <p className="text-2xl font-bold font-mono-data" style={{ color: "#40B6A2" }}>
                {spot != null ? `R$ ${fmt4(spot)}` : "—"}
              </p>
              <p className="text-[10px] text-[#6b89a8] font-mono-data mt-2">Tempo real · ValorPro</p>
            </div>

            {/* NDF em Aberto — posições reportadas pelos bancos.
                Ajuste = volume × (PTAX do sistema − quote), recalculado a cada
                troca de data; nada aqui entra nos totais da Posição do Dia. */}
            <div className="border border-[#123a5e] rounded-sm overflow-hidden" data-testid="painel-ndf-bancario">
              <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
                <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">NDF em Aberto · Bancos</h2>
                {/* Sem botão próprio: alimentado pelo "Importar NDF" do
                    cabeçalho da página, que lê o mesmo arquivo. */}
              </div>

              {!ndfBancario?.itens?.length ? (
                <p className="p-4 text-xs text-[#6b89a8]">
                  {ndfBancario?.vencimento
                    ? `Nenhuma posição com vencimento em ${fmtDateBr(ndfBancario.vencimento)}.`
                    : "Nenhuma posição importada."}{" "}
                  Use “Importar NDF” no topo da página para enviar a planilha NDF em Aberto.
                </p>
              ) : (
                <>
                  <div className="px-4 py-2 border-b border-[#0e2e4e] flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-[#6b89a8]">
                      PTAX {fmtDateBr(ndfBancario.data)}
                      {ndfBancario.vencimento && ` · venc. ${fmtDateBr(ndfBancario.vencimento)}`}
                    </span>
                    <span className="text-[11px] font-mono-data" style={{ color: ndfBancario.ptax_venda != null ? "#8aa9c9" : "#FFB800" }}>
                      {ndfBancario.ptax_venda != null ? `R$ ${fmt4(ndfBancario.ptax_venda)}` : "indisponível"}
                      {ndfBancario.ptax_fallback_data && ` · ref. ${fmtDateBr(ndfBancario.ptax_fallback_data)}`}
                      {!ndfBancario.ptax_fallback_data && ndfBancario.ptax_parcial && " · parcial"}
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#061729] border-b border-[#123a5e]">
                        <tr className="text-[9px] tracking-[0.15em] uppercase text-[#6b89a8]">
                          <th className="p-2 text-left">Banco</th>
                          <th className="p-2 text-right">Volume</th>
                          <th className="p-2 text-right">Quote</th>
                          <th className="p-2 text-center">Ref.</th>
                          <th className="p-2 text-right">Ajuste</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ndfBancario.itens.map((it) => (
                          <tr key={it.id} className="border-b border-[#0e2e4e]" data-testid={`ndf-banc-row-${it.id}`}>
                            <td className="p-2">
                              <span className="text-white">{it.banco}</span>
                              {!it.requester_action && (
                                <span className="block text-[9px] text-[#FFB800]" title="Sem Buy/Sell na planilha — fora do ajuste">
                                  sem ação
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right font-mono-data"
                              style={{
                                // Sem Buy/Sell o sinal não existe: cinza, para não
                                // sugerir posição comprada que a planilha não afirma.
                                color: !it.requester_action ? "#6b89a8"
                                  : it.volume_usd < 0 ? "#FF3B30" : "#00FF66",
                              }}>
                              {fmt2(it.volume_usd)}
                            </td>
                            <td className="p-2 text-right font-mono-data text-[#8aa9c9]">{fmt4(it.quote)}</td>
                            <td className="p-2 text-center font-mono-data text-[#6b89a8]">{fmtDateBr(it.effective_date)}</td>
                            <td className="p-2 text-right font-mono-data"
                              style={{ color: it.ajuste == null ? "#6b89a8" : it.ajuste < 0 ? "#FF3B30" : "#00FF66" }}>
                              {it.ajuste != null ? fmt2(it.ajuste) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-[#123a5e] font-bold bg-[#0B2D4D]">
                          <td className="p-2 text-[10px] tracking-wider uppercase">Total</td>
                          <td className="p-2 text-right font-mono-data text-[#8aa9c9]">{fmt2(ndfBancario.total_volume_usd)}</td>
                          <td colSpan={2}></td>
                          <td className="p-2 text-right font-mono-data"
                            style={{ color: ndfBancario.total_ajuste < 0 ? "#FF3B30" : "#00FF66" }}>
                            {fmt2(ndfBancario.total_ajuste)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {ndfBancario.sem_acao > 0 && (
                    <p className="px-4 py-2 text-[10px] text-[#FFB800] border-t border-[#0e2e4e]">
                      {ndfBancario.sem_acao} posição(ões) sem Buy/Sell na planilha — exibidas, mas fora do total do ajuste.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

        </div>


      </div>

      {/* Modal — Calculadora Financeira */}
      {calcOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setCalcOpen(false)}
        >
          <div
            className="w-full max-w-2xl border border-[#123a5e] bg-[#0B2D4D] rounded-sm"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#123a5e]">
              <div className="flex items-center gap-2">
                <Calculator size={16} color="#40B6A2" />
                <h2 className="text-sm tracking-[0.3em] uppercase text-[#8aa9c9] font-semibold">
                  Calculadora Financeira
                </h2>
              </div>
              <button onClick={() => setCalcOpen(false)} className="text-[#6b89a8] hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Grid de cálculo — 3 colunas: label | modo 1 | modo 2 */}
              <div className="grid gap-y-2" style={{ gridTemplateColumns: "auto 1fr 1fr", columnGap: "16px" }}>

                {/* Cabeçalhos dos modos */}
                <div />
                <div className="text-center text-[10px] tracking-[0.2em] uppercase text-[#6b89a8] pb-2 border-b border-[#123a5e] mb-1">
                  Calcular Juros a.m.
                </div>
                <div className="text-center text-[10px] tracking-[0.2em] uppercase text-[#6b89a8] pb-2 border-b border-[#123a5e] mb-1">
                  Calcular VF
                </div>

                {/* Valor Presente */}
                <label className="text-xs text-[#8aa9c9] uppercase tracking-wider whitespace-nowrap flex items-center pr-2">Valor Presente</label>
                <input type="number" step="0.0001" value={calc.vp1}
                  onChange={e => setF("vp1", e.target.value)}
                  placeholder="0,0000"
                  className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm text-right font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] w-full" />
                <input type="number" step="0.0001" value={calc.vp2}
                  onChange={e => setF("vp2", e.target.value)}
                  placeholder="0,0000"
                  className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm text-right font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] w-full" />

                {/* Taxa de Juros a.m. */}
                <label className="text-xs text-[#8aa9c9] uppercase tracking-wider whitespace-nowrap flex items-center pr-2">Taxa Juros a.m.</label>
                {/* Modo 1: resultado calculado */}
                <div className="rounded-sm px-3 py-1.5 text-sm text-right font-mono-data"
                  style={{ background: "rgba(255,184,0,0.06)", border: "1px solid rgba(255,184,0,0.25)", color: am1 != null ? "#FFB800" : "#3a4a5a" }}>
                  {am1 != null ? fmtPct(am1) : "—"}
                </div>
                {/* Modo 2: input (destaque amarelo) */}
                <input type="number" step="0.0001" value={calc.taxa2}
                  onChange={e => setF("taxa2", e.target.value)}
                  placeholder="0,80"
                  className="rounded-sm px-3 py-1.5 text-sm text-right font-mono-data focus:outline-none w-full"
                  style={{ background: "rgba(255,184,0,0.06)", border: "1px solid rgba(255,184,0,0.4)", color: "#FFB800" }} />

                {/* Período */}
                <label className="text-xs text-[#8aa9c9] uppercase tracking-wider whitespace-nowrap flex items-center pr-2">Período (meses)</label>
                <input type="number" step="0.001" value={calc.periodo}
                  onChange={e => setF("periodo", e.target.value)}
                  placeholder="1,000"
                  className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm text-right font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] w-full" />
                <input type="number" step="0.001" value={calc.periodo}
                  onChange={e => setF("periodo", e.target.value)}
                  placeholder="1,000"
                  className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm text-right font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] w-full" />

                {/* Valor Futuro */}
                <label className="text-xs text-[#8aa9c9] uppercase tracking-wider whitespace-nowrap flex items-center pr-2">Valor Futuro</label>
                {/* Modo 1: input */}
                <input type="number" step="0.0001" value={calc.vf1}
                  onChange={e => setF("vf1", e.target.value)}
                  placeholder="0,0000"
                  className="bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm text-right font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2] w-full" />
                {/* Modo 2: resultado calculado */}
                <div className="rounded-sm px-3 py-1.5 text-sm text-right font-mono-data"
                  style={{ background: "rgba(255,184,0,0.06)", border: "1px solid rgba(255,184,0,0.25)", color: vf2Computed != null ? "#FFB800" : "#3a4a5a" }}>
                  {vf2Computed != null ? vf2Computed.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : "—"}
                </div>

                {/* Taxa ao Ano */}
                <label className="text-xs text-[#8aa9c9] uppercase tracking-wider whitespace-nowrap flex items-center pr-2">Taxa ao Ano</label>
                <div className="px-3 py-1.5 text-sm text-right font-mono-data text-white">
                  {aa1 != null ? fmtPct(aa1, 2) : "—"}
                </div>
                <div className="px-3 py-1.5 text-sm text-right font-mono-data text-white">
                  {aa2 != null ? fmtPct(aa2, 2) : "—"}
                </div>
              </div>

              {/* Seção de datas */}
              <div className="border-t border-[#123a5e] pt-4">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#6b89a8] mb-3">
                  Calcular período em meses pelas datas
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#6b89a8] uppercase tracking-wider block mb-1">Data Inicial</label>
                    <input type="date" value={calc.dataInicio}
                      onChange={e => setDateAndRecalc("dataInicio", e.target.value)}
                      className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]" />
                  </div>
                  <div>
                    <label className="text-xs text-[#6b89a8] uppercase tracking-wider block mb-1">Data Final</label>
                    <input type="date" value={calc.dataFim}
                      onChange={e => setDateAndRecalc("dataFim", e.target.value)}
                      className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-1.5 text-sm font-mono-data text-white focus:outline-none focus:ring-1 focus:ring-[#40B6A2]" />
                  </div>
                </div>
              </div>

              {/* Ações */}
              <div className="flex items-center justify-between border-t border-[#123a5e] pt-4">
                <button onClick={usarSpotCalc}
                  data-testid="btn-usar-spot-calc"
                  className="px-4 py-1.5 text-xs border border-[#40B6A2]/40 text-[#40B6A2] hover:bg-[#40B6A2]/10 transition-colors rounded-sm font-semibold tracking-wider uppercase">
                  Usar Spot Atual
                </button>
                <button onClick={limparCalc}
                  data-testid="btn-limpar-calc"
                  className="px-4 py-1.5 text-xs border border-[#123a5e] text-[#6b89a8] hover:text-white hover:border-[#6b89a8] transition-colors rounded-sm uppercase tracking-wider">
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
